import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../layout/default';
import { Breadcrumbs } from '../../components/library/Breadcrumbs';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { useParams, useNavigate } from 'react-router-dom';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { usePermission } from '../../container/permissionContext';
import cx from 'classnames';
import { useAuth } from '../../container/authContext';
import InitiationsManagementModal from '../../components/library/initiations-management-modal';
import useOutsideClick from '../../hook/useOutsideClick';

function CorporateLibrary() {
  const { user } = useAuth();
  const { form, folder } = useParams();
  const navigate = useNavigate();
  const { hasPermissionByCodeName, isSuperAdmin, isCompanySystemAdministrator } = usePermission();
  const { forms_info: { forms_in_progress_count, forms_assigned_count } = {} } = user ?? {};

  // Initiations management is available only to Super Admin and the Company System Administrator.
  const canManageInitiations = isSuperAdmin() || isCompanySystemAdministrator();
  const actionsMenuRef = useRef(null);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isInitiationsModalOpen, setIsInitiationsModalOpen] = useState(false);

  useOutsideClick(actionsMenuRef, () => setIsActionsMenuOpen(false));

  const [defaultIndex, setDefaultIndex] = useState(form === 'files' ? 1 : 0);
  const [breadcrumbs] = useState([
    {
      name: 'Company Library',
      url: '/corporate-library',
    },
    null,
    null,
  ]);
  const [defaultFolder, setDefaultFolder] = useState([
    {
      id: 'forms-available-to-me',
      name: 'Forms available to me',
      url: 'forms-available-to-me',
      isDisable: false,
    },
    {
      id: 'forms-assigned-to-me',
      name: 'Forms assigned to me',
      url: 'forms-assigned-to-me',
      isDisable: false,
    },
    {
      id: 'forms-in-progress',
      name: 'Forms in progress',
      url: 'forms-in-progress',
      isDisable: false,
    },
    {
      id: 'submissions',
      name: 'Submissions',
      url: 'submissions',
      isDisable: false,
    },
    {
      id: 'public-forms',
      name: 'Public forms',
      url: 'public-forms',
      isDisable: false,
    },
  ]);

  useEffect(() => {
    if (hasPermissionByCodeName('corporate_library_management')) {
      setDefaultFolder([
        {
          id: 'forms-available-to-me',
          name: 'Forms available to me',
          url: 'forms-available-to-me',
          isDisable: false,
        },
        {
          id: 'forms-assigned-to-me',
          name: 'Forms assigned to me',
          url: 'forms-assigned-to-me',
          isDisable: false,
        },
        {
          id: 'forms-in-progress',
          name: 'Forms in progress',
          url: 'forms-in-progress',
          isDisable: false,
        },
        {
          id: 'submissions',
          name: 'Submissions',
          url: 'submissions',
          isDisable: false,
        },
        {
          id: 'public-forms',
          name: 'Public forms',
          url: 'public-forms',
          isDisable: false,
        },
        {
          id: 'drafts',
          name: 'Drafts',
          url: 'drafts',
          isDisable: false,
        },
      ]);
    }

    if (isSuperAdmin() || isCompanySystemAdministrator()) {
      setDefaultFolder([
        {
          id: 'forms-available-to-me',
          name: 'Forms available to me',
          url: 'forms-available-to-me',
          isDisable: false,
        },
        {
          id: 'forms-assigned-to-me',
          name: 'Forms assigned to me',
          url: 'forms-assigned-to-me',
          isDisable: false,
        },
        {
          id: 'submissions',
          name: 'Submissions',
          url: 'submissions',
          isDisable: false,
        },
        {
          id: 'public-forms',
          name: 'Public forms',
          url: 'public-forms',
          isDisable: false,
        },
        {
          id: 'drafts',
          name: 'Drafts',
          url: 'drafts',
          isDisable: false,
        },
      ]);
    }
  }, [hasPermissionByCodeName, isCompanySystemAdministrator, isSuperAdmin]);

  const goToPrev = (id) => navigate(id, { replace: true });

  const handlerChangeDefaultFolder = (folder) => navigate(`/corporate-library/forms/${folder.url}`);

  return (
    <Layout title="Company Library">
      <div className="library">
        <DndProvider backend={HTML5Backend}>
          <Breadcrumbs breadcrumbs={breadcrumbs} goToLevel={goToPrev} />
        </DndProvider>
        <div className="library-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 className="library-title">Company Library </h2>

          {canManageInitiations && (
            <div ref={actionsMenuRef} className="library-actions" style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn btn-outline btn-small"
                style={{ width: 44, height: 44, padding: 0 }}
                onClick={() => setIsActionsMenuOpen((prevState) => !prevState)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M9.99984 10.832C10.4601 10.832 10.8332 10.4589 10.8332 9.9987C10.8332 9.53846 10.4601 9.16536 9.99984 9.16536C9.5396 9.16536 9.1665 9.53846 9.1665 9.9987C9.1665 10.4589 9.5396 10.832 9.99984 10.832Z"
                    fill="#667085"
                    stroke="#667085"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.99984 4.9987C10.4601 4.9987 10.8332 4.6256 10.8332 4.16536C10.8332 3.70513 10.4601 3.33203 9.99984 3.33203C9.5396 3.33203 9.1665 3.70513 9.1665 4.16536C9.1665 4.6256 9.5396 4.9987 9.99984 4.9987Z"
                    fill="#667085"
                    stroke="#667085"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.99984 16.6654C10.4601 16.6654 10.8332 16.2923 10.8332 15.832C10.8332 15.3718 10.4601 14.9987 9.99984 14.9987C9.5396 14.9987 9.1665 15.3718 9.1665 15.832C9.1665 16.2923 9.5396 16.6654 9.99984 16.6654Z"
                    fill="#667085"
                    stroke="#667085"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {isActionsMenuOpen && (
                <div
                  className="library-item-menu"
                  style={{ zIndex: 999, right: 0, left: 'initial', top: '100%', marginTop: 4, whiteSpace: 'nowrap' }}
                >
                  <button
                    className="btn btn-full"
                    onClick={() => {
                      setIsActionsMenuOpen(false);
                      setIsInitiationsModalOpen(true);
                    }}
                  >
                    <i className="icon-settings mr-2" />
                    Initiations management
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/*<TabsMobile />*/}
        <h2 className="library-title-index-mobile">Company Library</h2>

        <Tabs selectedIndex={defaultIndex} onSelect={(index) => setDefaultIndex(index)}>
          <TabList>
            <Tab
              onClick={() => {
                navigate('/corporate-library/forms');
              }}
            >
              Forms
            </Tab>
            <Tab
              onClick={() => {
                navigate('/corporate-library/files');
              }}
            >
              Files
            </Tab>
          </TabList>

          <TabPanel>
            {folder && (
              <div className="library-filter">
                <div className="library-filter-search form-control-item">
                  <input type="text" className="form-control" placeholder="Search" />
                  <i className="icon-search"></i>
                </div>
                <div>
                  <button className="btn-outline btn">
                    <i className="icon-icon-sliders"></i>
                  </button>
                </div>
              </div>
            )}
            <h3 className="library-subtitle">Folders</h3>
            <div className="library-wrapper">
              {!folder &&
                defaultFolder.map((item) => {
                  let formsCount;

                  if (item.id === 'forms-assigned-to-me' || item.id === 'forms-in-progress') {
                    if (item.id === 'forms-assigned-to-me') {
                      formsCount = forms_assigned_count;
                    } else {
                      // Hide folder if user has no forms in progress
                      if (!forms_in_progress_count) return null;
                      formsCount = forms_in_progress_count;
                    }
                  }

                  return (
                    <div
                      key={item.id}
                      className={cx('library-item cursor-pointer', item.id, { 'library-item-disabled': item.isDisable })}
                      onClick={() => {
                        if (!item.isDisable) handlerChangeDefaultFolder(item);
                      }}
                    >
                      {!!formsCount && <span className={'forms-count'}>{formsCount}</span>}
                      <div className="library-item-text">
                        <img src="/images/folder-big-icon.png" className="mr-2" alt="" />
                        {item.name}
                      </div>
                    </div>
                  );
                })}
            </div>
          </TabPanel>
          <TabPanel>
            <h2>Files</h2>
          </TabPanel>
        </Tabs>
      </div>

      {canManageInitiations && (
        <InitiationsManagementModal isOpen={isInitiationsModalOpen} onClose={() => setIsInitiationsModalOpen(false)} />
      )}
    </Layout>
  );
}

export default CorporateLibrary;
